// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import api from './api'

export interface Deal {
  id: string
  customer_id: string
  customer: {
    id: string
    customer_id: string
    full_name: string
    company_name?: string
  }
  title: string
  name?: string
  description?: string
  amount: number
  equipment_price?: number
  deal_type: string
  maintenance_percentage?: number
  maintenance_amount?: number
  total_with_maintenance: number
  start_date?: string
  end_date?: string
  renewal_date?: string
  is_recurring: boolean
  billing_frequency?: string
  days_until_expiry?: number
  days_until_renewal?: number
  expired?: boolean
  renewal_due?: boolean
  has_invoices: boolean
  total_invoiced_amount: number
  remaining_amount: number
  fully_invoiced?: boolean
  created_at: string
  updated_at: string
  terms_and_conditions?: string
  notes?: string
  created_by?: {
    id: string
    name: string
    email: string
  }
  updated_by?: {
    id: string
    name: string
    email: string
  }
  invoices?: Invoice[]
}

export interface Invoice {
  id: string
  invoice_number: string
  total_amount: number
  subtotal: number
  paid_amount: number
  balance_due: number
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'cancelled'
  payment_status: 'unpaid' | 'partial' | 'paid' | 'refunded'
  issue_date: string
  due_date: string
  paid_date?: string
  currency: string
  description?: string
  overdue: boolean
  days_overdue?: number
  has_file: boolean
  file_url?: string
  created_at: string
  updated_at: string
}

export interface CreateDealData {
  customer_id: string
  title: string
  description?: string
  amount: number
  equipment_price?: number
  maintenance_percentage?: number
  deal_type?: string
  start_date?: string
  end_date?: string
  renewal_date?: string
  terms_and_conditions?: string
  notes?: string
  is_recurring?: boolean
  billing_frequency?: string
}

interface UpdateDealData {
  title?: string
  description?: string
  amount?: number
  equipment_price?: number
  maintenance_percentage?: number
  deal_type?: string
  start_date?: string
  end_date?: string
  renewal_date?: string | null
  terms_and_conditions?: string
  notes?: string
  is_recurring?: boolean
  billing_frequency?: string
}

export const dealsApi = {
  /**
   * Get all deals with optional filters
   */
  getDeals: async (params?: {
    customer_id?: string
    deal_type?: string
    search?: string
    page?: number
    per_page?: number
  }): Promise<{
    customer_deals: Deal[]
    pagination: {
      current_page: number
      per_page: number
      total_count: number
      total_pages: number
    }
  }> => {
    const query = new URLSearchParams()
    if (params?.customer_id) query.append('customer_id', params.customer_id)
    if (params?.deal_type) query.append('deal_type', params.deal_type)
    if (params?.search) query.append('search', params.search)
    if (params?.page) query.append('page', params.page.toString())
    if (params?.per_page) query.append('per_page', params.per_page.toString())

    const url = params?.customer_id 
      ? `/customers/${params.customer_id}/deals?${query.toString()}`
      : `/customer_deals?${query.toString()}`
      
    const response = await api.get(url)
    return response.data
  },

  /**
   * Get a single deal by ID
   */
  getDeal: async (dealId: string): Promise<Deal> => {
    const response = await api.get(`/customer_deals/${dealId}`)
    return response.data.customer_deal
  },

  /**
   * Create a new deal
   */
  createDeal: async (dealData: CreateDealData): Promise<Deal> => {
    const url = dealData.customer_id 
      ? `/customers/${dealData.customer_id}/deals`
      : '/customer_deals'
      
    const response = await api.post(url, { customer_deal: dealData })
    return response.data.customer_deal
  },

  /**
   * Update an existing deal
   */
  updateDeal: async (dealId: string, dealData: UpdateDealData): Promise<Deal> => {
    const response = await api.put(`/customer_deals/${dealId}`, { customer_deal: dealData })
    return response.data.customer_deal
  },

  /**
   * Force send renewal reminder for a deal
   */
  sendRenewalReminder: async (dealId: string): Promise<{
    message: string
    recipients_count: number
    recipients?: string[]
    days_until_renewal: number
  }> => {
    const response = await api.post(`/customer_deals/${dealId}/send_renewal_reminder`)
    return response.data
  }
}

